/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useMemo} from 'react';
import {setRel} from '@ion/core/src/services/ConnectUtils';
import {GenericObject} from '@ion/core/src/types/entities';
import {useAccountApplications} from '@ion/query/src/hooks/useAccountApplications';

export interface UseDefaultApplications {
  accountId: number;
  applicationUUIDs?: string[];
  enabled?: boolean;
  staleTime?: number;
};

export interface InitialApplications {
  applications: GenericObject[];
  applicationUUIDs: string[];
  isEnabled: boolean;
};

export type UseAccountDefaultApplicationsResult = {
  applications: {
    appNames: string[];
    appHrefs: string[];
  };
  isLoading: boolean;
  error: any;
};

export const ROOT_ACCOUNT_ID = 2;

export const CTC_APPLICATIONS = {
  ADM              : '820b5258-1f73-4d7b-bd72-c75c4e5072d1',
  CONNECT_API_DOCS : 'd8b98aad-cc6e-4899-a86d-e2848eaf03b4'
};

/**
 * Defines a custom React hook to fetch and filter applications for an account, which can be used
 * to set default applications when creating subaccounts in the CTC platform. It's tailored
 * for the scenario where subaccounts need to inherit applications from their parent accounts,
 * ensuring permission consistency and expected functionality.
 *
 * The hook integrates with the `useAccountApplications` hook to fetch applications based on
 * the provided account ID and uses memoization to avoid unnecessary re-renders. It can be
 * configured to return all applications or only a subset based on provided UUIDs, which
 * allows for fine-grained control over which applications subaccounts should have access to.
 *
 * The hook handles cases where the parent account might not have certain default applications,
 * thereby preventing the API from returning an error response when attempting to create a
 * subaccount with an application set that differs from the parent account.
 *
 * ```typescript
 * import {useAccountDefaultApplications} from './hooks/useAccountDefaultApplications';
 *
 * ...
 *
 * const { applications, isLoading, error } = useAccountDefaultApplications({
 *   accountId: globalGroupId,
 *   applicationUUIDs: ['UUID1', 'UUID2'], // Optional UUIDs to filter the applications
 *   staleTime: 0,
 *   enabled: true
 * });
 *
 * ```
 *
 * @param {number} accountId - The ID of the account for which to retrieve applications.
 * @param {string[]} applicationUUIDs - Optional list of application UUIDs to filter the results.
 * @param {number} staleTime - Optional to configure the stale time for the query.
 * @param {boolean} enabled - Flag to enable or disable the query.
 * @returns - The applications result object containing the applications data, loading state, and any errors.
 */
export const useAccountDefaultApplications = ({
  accountId,
  staleTime,
  applicationUUIDs = [],
  enabled = true
}: UseDefaultApplications): UseAccountDefaultApplicationsResult => {
  const {applications, isLoading, error} = useAccountApplications(
    accountId,
    accountId !== ROOT_ACCOUNT_ID && enabled,
    staleTime
  );

  const filteredApplications = useMemo(() => getInitialApplications({
    applications,
    applicationUUIDs,
    isEnabled : enabled
  }), [applications, applicationUUIDs, accountId]);

  return {
    applications : filteredApplications || {appNames: [], appHrefs: []},
    isLoading,
    error
  };
};

const getInitialApplications = ({
  applications,
  applicationUUIDs,
  isEnabled = true
}: InitialApplications) => {
  if (isEnabled) {
    const applicationKeysToFilter = applicationUUIDs?.length > 0 ? applicationUUIDs : Object.values(CTC_APPLICATIONS);
    const appNames = [];
    const appHrefs = [];
    if (Array.isArray(applications)) {
      applications.forEach((application) => {
        const {name, applicationKey} = application;
        if (applicationKeysToFilter.includes(applicationKey)) {
          const hrefObj = setRel(application, 'application');
          appNames.push(name);
          appHrefs.push({
            ...hrefObj,
            title : name
          });
        }
      });
    }
    return {appNames, appHrefs};
  }
  return {appNames: [], appHrefs: []};
};
